import { Injectable } from '@angular/core';
import { Message, OpenApiService } from '../../../core/services/open-api.service';
import { OpenAiService } from '../../../core/services/open-ai.service';
import {
  CODING_LANGUAGES, CodingAnswer, CodingLanguage, CodingReply, CodingState, codingAnswerToMarkdown, containsPastedCode,
  isFailedCodingReply,
  parseCodingAnswer,
  parseCodingReply, streamingCodingAnswer
} from './coding-answer.model';
import { parsePartialJson } from './partial-json';
import { Typewriter } from './typewriter';
import { CODING_MODE_PROMPT } from '../../templates/components/template-form/template-form.component';

/**
 * Model used for every coding-mode answer. gpt-6-luna takes text + image input on the Responses API,
 * supports structured outputs, prompt caching and reasoning.effort none..max (default medium).
 */
export const CODING_MODEL = 'gpt-6-luna';

/** Used when OpenAI is unavailable (no key, auth, outage), the same fallback the normal chat uses. */
export const CODING_FALLBACK_MODEL = 'anthropic-claude-sonnet-4-6';

/**
 * Reasoning effort. Speed matters most in a live interview, so the model writes immediately (`none`, the
 * fastest setting gpt-6-luna supports) instead of thinking for several seconds first. Raise to `low` if
 * bug-finding accuracy ever matters more than the first-word latency.
 */
export const CODING_REASONING_EFFORT = 'none';

/** Generous cap so long programs are never cut off. */
export const CODING_MAX_OUTPUT_TOKENS = 32000;

/** Standard high detail reads code in a screenshot accurately and is faster than full original resolution. */
export const CODING_IMAGE_DETAIL = 'high';

/** After OpenAI fails, coding requests go straight to Claude for this long instead of waiting for it to fail again. */
export const OPENAI_RETRY_AFTER_MS = 10 * 60 * 1000;


/** Same window as the normal chat's raw context: the last four question/answer pairs. */
export const CODING_HISTORY_PAIRS = 4;
const MAX_USER_TURN_CHARS = 800;
const MAX_ASSISTANT_TURN_CHARS = 4000;
const MAX_CODING_TURN_CHARS = 8000;

const FILE_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['id', 'path', 'language', 'content'],
  properties: {
    id: { type: 'string', description: 'Stable identifier, unchanged across renames, e.g. "main".' },
    path: { type: 'string', description: 'Relative filename such as Main.java or src/app.py.' },
    language: { type: 'string', enum: [...CODING_LANGUAGES] },
    content: { type: 'string', description: 'The complete, exact file content.' }
  }
};

/**
 * Strict Responses API output schema, so the model cannot drift from the contract the UI validates. Structured
 * outputs are written in this key order, so `newProblem` (and `baseline`, before `files`) arrive before any code
 * and the streaming answer is drawn the way the finished one will be.
 */
export const CODING_REPLY_FORMAT = {
  type: 'json_schema',
  name: 'coding_answer',
  strict: true,
  schema: {
    type: 'object',
    additionalProperties: false,
    required: ['newProblem', 'language', 'problem', 'clarification', 'summary', 'baseline', 'files', 'deletedFileIds', 'notes'],
    properties: {
      newProblem: { type: 'boolean', description: 'true for a new, separate question; false only when changing the current code.' },
      language: { type: 'string', enum: [...CODING_LANGUAGES] },
      problem: { type: 'string' },
      clarification: { type: 'string' },
      summary: { type: 'string' },
      baseline: { anyOf: [{ type: 'null' }, { type: 'array', items: FILE_SCHEMA }] },
      files: { type: 'array', items: FILE_SCHEMA },
      deletedFileIds: { type: 'array', items: { type: 'string' } },
      notes: {
        type: 'array',
        items: {
          type: 'object',
          additionalProperties: false,
          required: ['fileId', 'anchor', 'text'],
          properties: {
            fileId: { type: 'string', description: 'The id of the file in "files" this note belongs to, e.g. "main".' },
            anchor: { type: 'string', description: 'One changed line copied exactly from that file ("" for the whole file).' },
            text: { type: 'string' }
          }
        }
      }
    }
  }
};

/** Claude's equivalent of the strict JSON schema: a tool it is forced to call with exactly this input shape. */
export const CODING_ANSWER_TOOL = {
  name: 'submit_coding_answer',
  description: 'Submit the coding answer: the complete resulting files, any transcribed baseline, and one plain-English note per change.',
  input_schema: CODING_REPLY_FORMAT.schema as Record<string, unknown>
};

/** What the chat knows about this interview. */
export interface CodingHints {
  /** The template's saved main prompt (promptTemplate in template-form); CODING_MODE_PROMPT is added after it. */
  mainPrompt?: string;
  /** Role and job description, used to pick the default language without ever asking the helper. */
  role?: string;
  jobDescription?: string;
}

/**
 * The language for a new problem when the request names none and no code is shown: the interview's language from
 * the role title (checked first) or job description, limited to the languages coding interviews are usually held in
 * (Java, Python, C, C++). Anything else, including full-stack and JavaScript roles, falls back to Python.
 */
export function defaultCodingLanguage(hints: CodingHints = {}): CodingLanguage {
  for (const text of [hints.role || '', hints.jobDescription || '']) {
    const lower = text.toLowerCase();
    if (/\bjava\b(?!\s*script)/.test(lower)) return 'java';
    if (/\bpython\b|\bdjango\b|\bflask\b|\bfastapi\b/.test(lower)) return 'python';
    if (/c\+\+|\bcpp\b/.test(lower)) return 'cpp';
    if (/\bembedded c\b|\bc programming\b|\bc developer\b|\bc language\b/.test(lower)) return 'c';
  }
  return 'python';
}

type Turn = { role: 'user' | 'assistant'; content: string };

const IMAGE_MEDIA_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

/**
 * Works out an image's real format from its first bytes. Uploads are stored in S3 under a .jpg name and,
 * historically, as application/octet-stream, so neither the name nor the served type can be trusted.
 */
export function detectImageMediaType(head: Uint8Array, servedType = ''): string {
  const starts = (...bytes: number[]) => bytes.every((byte, i) => head[i] === byte);
  if (starts(0x89, 0x50, 0x4e, 0x47)) return 'image/png';
  if (starts(0xff, 0xd8, 0xff)) return 'image/jpeg';
  if (starts(0x47, 0x49, 0x46, 0x38)) return 'image/gif';
  if (starts(0x52, 0x49, 0x46, 0x46) && head[8] === 0x57 && head[9] === 0x45 && head[10] === 0x42 && head[11] === 0x50) {
    return 'image/webp';
  }
  return IMAGE_MEDIA_TYPES.includes(servedType) ? servedType : 'image/png';
}

const stripHtml = (text: string): string => text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
const clip = (text: string, max: number): string => text.length > max ? text.slice(0, max) + '\n[...truncated]' : text;
const describe = (error: unknown): string => error instanceof Error ? error.message : typeof error === 'string' ? error : 'unknown error';

/**
 * The same question/answer window the normal chat sends (last four pairs, oversized user turns skipped),
 * with coding answers rendered as readable markdown instead of their JSON envelope.
 */
export function buildCodingHistory(messages: ReadonlyArray<Message>, maxPairs = CODING_HISTORY_PAIRS): Turn[] {
  const turns: Turn[] = [];
  let pairs = 0;
  let pendingBot: string | null = null;
  for (let i = messages.length - 1; i >= 0 && pairs < maxPairs; i--) {
    const m = messages[i];
    if (m.sender === 'bot' && m.text) {
      // A failed or cancelled reply is not an answer: drop it and the request that produced it.
      if (isFailedCodingReply(m.text)) {
        pendingBot = null;
        continue;
      }
      const answer = parseCodingAnswer(m.text);
      if (answer?.streaming) {
        pendingBot = null;
        continue;
      }
      pendingBot = answer
        ? clip('[Coding mode answer]\n' + codingAnswerToMarkdown(answer), MAX_CODING_TURN_CHARS)
        : clip(m.text, MAX_ASSISTANT_TURN_CHARS);
    } else if (m.sender === 'user' && m.text) {
      const hadImage = /<img\b/i.test(m.text);
      const plain = stripHtml(m.text);
      if (plain.length > MAX_USER_TURN_CHARS) {
        pendingBot = null;
        continue;
      }
      if (pendingBot !== null) {
        turns.unshift({ role: 'assistant', content: pendingBot });
        turns.unshift({ role: 'user', content: (plain || 'Screenshot') + (hadImage ? ' (screenshot attached)' : '') });
        pairs++;
        pendingBot = null;
      }
    }
  }
  return turns;
}

@Injectable({ providedIn: 'root' })
export class CodingAiService {
  /** Which provider produced the most recent answer ('openai' or 'vertex-ai-claude'); for diagnostics. */
  lastProvider: 'openai' | 'vertex-ai-claude' | '' = '';

  constructor(private ai: OpenApiService, private vertex: OpenAiService) {}

  /** When OpenAI last failed; while recent, coding requests go straight to Claude. */
  private openAiFailedAt = 0;

  /**
   * Asks the coding model for the next answer. `state.files` is the code from the previous coding answer;
   * `history` is the conversation so far (both normal and coding answers) so follow-ups keep their context.
   * The reply streams in: `onPartial` receives a displayable answer every few dozen milliseconds, and the
   * returned promise resolves with the complete, validated reply. Falls back to Claude on Vertex AI when OpenAI
   * cannot be used, exactly like the normal chat. `signal` cancels the request.
   */
  async propose(state: CodingState, request: string, imageUrl: string, hints: CodingHints = {},
      history: ReadonlyArray<Message> = [], onPartial?: (answer: CodingAnswer) => void,
      signal?: AbortSignal): Promise<CodingReply> {
    // The main prompt, with the coding-mode prompt added after it because coding mode is on.
    const mainPrompt = [hints.mainPrompt, CODING_MODE_PROMPT].filter(Boolean).join('\n\n');
    const conversationState = this.ai.getConversationState();
    const stateNote = conversationState
      ? 'Conversation state kept by the chat (topic, artifacts already shared, pending work). Use it only to '
        + 'understand what the learner is working on:\n' + JSON.stringify(conversationState)
      : '';
    const turns = buildCodingHistory(history);
    const requestText = 'Request data:\n' + JSON.stringify({
      request,
      hasScreenshot: Boolean(imageUrl),
      problem: state.problem,
      defaultLanguage: defaultCodingLanguage(hints),
      currentFilesLanguage: state.language,
      previousCodingRequests: state.requests,
      currentFiles: state.files
    });

    // Code can only be "read" from a screenshot or from code in the message itself; otherwise a baseline is ignored.
    const allowBaseline = Boolean(imageUrl) || containsPastedCode(request);

    let openAiError: unknown = 'skipped: OpenAI failed recently';
    if (Date.now() - this.openAiFailedAt > OPENAI_RETRY_AFTER_MS) {
      let text = '';
      try {
        text = await this.typed(state, allowBaseline, onPartial, signal,
          push => this.askOpenAI(mainPrompt, stateNote, turns, requestText, imageUrl, push, signal));
        this.openAiFailedAt = 0;
      } catch (error) {
        if (signal?.aborted) throw error;
        openAiError = error;
        // OpenAI could not be reached (key, auth, outage): use Claude for a while instead of waiting for it to fail.
        this.openAiFailedAt = Date.now();
        console.warn('[CodingMode] OpenAI unavailable, falling back to Vertex AI Claude:', describe(error));
      }
      if (text) {
        try {
          const reply = parseCodingReply(text, state.files, allowBaseline);
          this.lastProvider = 'openai';
          return reply;
        } catch (error) {
          // OpenAI answered but the answer could not be used: retry this request with Claude, keep using OpenAI.
          openAiError = error;
          console.warn('[CodingMode] OpenAI reply was unusable, retrying with Vertex AI Claude:', describe(error),
            '\nReply:', text.slice(0, 6000));
        }
      }
    }

    let fallbackText = '';
    try {
      const system = [mainPrompt, stateNote,
        'OUTPUT: deliver the answer by calling the submit_coding_answer tool. Write its fields in the order of its '
        + 'schema: newProblem first, then language, problem, clarification, summary, baseline (null or an array of '
        + 'files), files, deletedFileIds and notes; "files" and "deletedFileIds" are always arrays.']
        .filter(Boolean).join('\n\n');
      fallbackText = await this.typed(state, allowBaseline, onPartial, signal,
        push => this.vertexWithImage(system, turns, requestText, imageUrl, push, signal));
      const reply = parseCodingReply(fallbackText, state.files, allowBaseline);
      this.lastProvider = 'vertex-ai-claude';
      return reply;
    } catch (fallbackError) {
      if (signal?.aborted) throw fallbackError;
      if (fallbackText) {
        console.warn('[CodingMode] Claude reply was unusable:', describe(fallbackError), '\nReply:', fallbackText.slice(0, 6000));
      }
      throw new Error(`${describe(openAiError)} The Claude fallback also failed: ${describe(fallbackError)}`);
    }
  }

  /**
   * Runs one streamed attempt and types the answer out smoothly while it arrives: the growing reply goes through
   * a Typewriter, each revealed prefix becomes a displayable answer for `onPartial`, and the attempt only
   * completes once the typing has caught up, so the finished answer never jumps ahead of what was shown.
   */
  private async typed(state: CodingState, allowBaseline: boolean, onPartial: ((answer: CodingAnswer) => void) | undefined,
      signal: AbortSignal | undefined, run: (push: (text: string) => void) => Promise<string>): Promise<string> {
    if (!onPartial) return run(() => undefined);
    const writer = new Typewriter(text => {
      const answer = streamingCodingAnswer(parsePartialJson(text), state, allowBaseline);
      if (answer) onPartial(answer);
    });
    const stop = () => writer.stop();
    signal?.addEventListener('abort', stop);
    try {
      const text = await run(partial => writer.push(partial));
      await writer.drain();
      return text;
    } finally {
      writer.stop();
      signal?.removeEventListener('abort', stop);
    }
  }

  /**
   * Streamed Responses API call: the main prompt first (static, so prompt-cache friendly), then the conversation,
   * then the request with the screenshot. Resolves with the complete reply text.
   */
  private async askOpenAI(mainPrompt: string, stateNote: string, turns: Turn[], requestText: string, imageUrl: string,
      push: (text: string) => void, signal?: AbortSignal): Promise<string> {
    const input: unknown[] = [];
    if (mainPrompt) input.push({ role: 'developer', content: mainPrompt });
    if (stateNote) input.push({ role: 'developer', content: stateNote });
    input.push(...turns);
    input.push({
      role: 'user',
      content: [
        { type: 'input_text', text: requestText },
        ...(imageUrl ? [{ type: 'input_image', image_url: imageUrl, detail: CODING_IMAGE_DETAIL }] : [])
      ]
    });
    const started = performance.now();
    let firstText = 0;
    const text = await this.ai.streamOpenAIResponse(input, {
      model: CODING_MODEL,
      textFormat: CODING_REPLY_FORMAT,
      reasoningEffort: CODING_REASONING_EFFORT,
      maxOutputTokens: CODING_MAX_OUTPUT_TOKENS
    }, partialText => {
      if (!firstText) firstText = performance.now() - started;
      push(partialText);
    }, signal);
    console.log(`[CodingMode] ${CODING_MODEL} | first text ${Math.round(firstText)}ms | total ${Math.round(performance.now() - started)}ms`);
    return text;
  }

  /**
   * Vertex call. Claude on Vertex AI does not fetch image URLs, so the screenshot is downloaded and sent inline
   * with its real media type. Claude is forced through CODING_ANSWER_TOOL, so its reply follows the same schema
   * OpenAI is held to.
   */
  private async vertexWithImage(system: string, turns: Turn[], requestText: string, imageUrl: string,
      push: (text: string) => void, signal?: AbortSignal): Promise<string> {
    const options = { modelKey: CODING_FALLBACK_MODEL, tool: CODING_ANSWER_TOOL };
    let image: unknown;
    if (imageUrl) {
      const inline = await this.fetchImageAsBase64(imageUrl, signal);
      image = { type: 'image', source: { type: 'base64', media_type: inline.mediaType, data: inline.data } };
    }
    const messages = [
      ...turns,
      { role: 'user' as const, content: [{ type: 'text', text: requestText }, ...(image ? [image] : [])] }
    ];
    return this.vertex.streamVertexClaudeMessage(system, messages, options, push, signal);
  }

  private async fetchImageAsBase64(url: string, signal?: AbortSignal): Promise<{ mediaType: string; data: string }> {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Could not download the screenshot (HTTP ${response.status}).`);
    const blob = await response.blob();
    const head = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
    const data = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
      reader.onerror = () => reject(new Error('Could not read the screenshot.'));
      reader.readAsDataURL(blob);
    });
    return { mediaType: detectImageMediaType(head, blob.type), data };
  }
}
